import * as THREE from "three";

// The rider's look and bone lengths (#75: split out of `skater-parts.ts`),
// shared by the body-region builders here and the pose code.

/**
 * What the rider looks like, by part, so create-a-skater can swap pieces
 * (hair, top, bottoms, tattoos) rather than just recolor them.
 */
export interface SkaterLook {
  skin: string;
  /**
   * Hair, or headwear over it; `color` is the hair (and brows), `hat` the
   * headwear's color. Long hair (#227) falls past the shoulders and swings;
   * a ponytail (#234) is tied up at the back of the head, its tail swinging.
   */
  hair: { style: "mohawk" | "cap" | "cowboy-hat" | "space-helmet" | "long" | "ponytail"; color: string; hat?: string };
  /**
   * A tank top leaves the arms bare; a tee has long sleeves; a T-shirt
   * (`tshirt`, #227) short ones, the forearms bare. A western shirt
   * adds pocket flaps and pearl snaps in `trim`; a space suit adds mission
   * patches and a chest pack in `trim`.
   */
  top: { style: "tank" | "tee" | "tshirt" | "western" | "spacesuit"; color: string; trim?: string };
  /**
   * Cut-offs stop above the knee (bare shins); pants go down to the shoes;
   * leggings (#227) do too, fitted and smooth; baggy (#234) is a loose skate
   * fit, wider than pants, tapering a little to a break over the shoes. A
   * `buckle` adds a belt with a big buckle.
   */
  bottoms: { style: "cutoffs" | "pants" | "leggings" | "baggy"; color: string; buckle?: string };
  /** Tattoo sleeves; only shows on a bare arm. */
  tattoos: { arm: "front" | "back"; ink: string }[];
  shoes: string;
  /** Skate shoes (the default), western boots or chunky moon boots. */
  shoeStyle?: "skate" | "boots" | "moon";
  /** A bandana knotted round the neck, in this color. */
  bandana?: string;
  /** Iris color; blue when unset. */
  eyes?: string;
  /**
   * Lip color (#239); unset, the skin blended toward red (`lipColor`), so
   * only a look that sets it changes.
   */
  lips?: string;
  /** A slightly narrower chest, waist and shoulders. */
  build?: "narrow";
  deck: string;
  wheels: string;
}

export const DEFAULT_LOOK: SkaterLook = {
  skin: "#f2c29b",
  hair: { style: "mohawk", color: "#ff4fb8" },
  top: { style: "tank", color: "#2ec4b6" },
  bottoms: { style: "cutoffs", color: "#5a7fc4" },
  tattoos: [{ arm: "front", ink: "#1f2a6e" }],
  shoes: "#f7f7f2",
  deck: "#ff3d8b",
  wheels: "#ffe14d",
};

/** Bone lengths, shared with the pose code's IK (skater-model.ts). */
export const THIGH = 0.36;
export const SHIN = 0.36;
/** Shoulder to elbow, and elbow to the palm; together the old one-piece arm's reach. */
export const UPPER_ARM = 0.23;
export const FOREARM = 0.23;
/** Where the feet sit on the deck, either side of the middle. */
export const FOOT_Z = 0.21;

export interface Arm {
  /** Pivots at the shoulder; the upper arm hangs down its -y. */
  shoulder: THREE.Group;
  /** Pivots at the elbow; the forearm and hand hang down its -y. */
  elbow: THREE.Group;
  hand: THREE.Group;
  /** Which way the elbow points when solving a reach, in torso space. */
  pole: THREE.Vector3;
}

export interface Leg {
  hip: THREE.Group;
  knee: THREE.Group;
  foot: THREE.Object3D;
  z: number;
}

/** Where the head sits in torso space, high enough to show a neck under the jaw (#80). */
export const HEAD_Y = 0.74;
/** The neck runs from inside the chest's shoulders up into the head. */
export const NECK_BASE = 0.42;
export const NECK_TOP = HEAD_Y - 0.1;
